import { FileUpload } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DatasetBox from '~/components/DatasetBox';
import ImageSummaryRow from '~/components/ImageItem/ImageSummaryRow';
import SectionTitle from '~/components/SectionTitle';
import {
    domainEventBus,
    RegistrationCreateRequestedEvent,
} from '~/domain/events';
import {
    frameRegistrationRefusal,
    sharesFrameOfReference,
} from '~/domain/dicom/uids';
import { Image3D } from '~/domain/patient/image3d';
import { reportImageDownloadFailure } from '~/hooks/image/reportImageDownloadFailure';
import useLoadAvailableImage from '~/hooks/image/useLoadAvailableImage';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import {
    useActivateRegistration,
    useRegistrationState,
} from '~/hooks/registration/useRegistrationStore';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import {
    NewRegistrationRow,
    RegistrationRow,
} from '~/components/RegistrationRow/RegistrationRows';

type Props = {
    primaryImage: Image3D;
    availableImages: AvailableImage[];
    selectedImageUid?: string;
    onSelectImage?: (image: AvailableImage) => void;
};

/**
 * The images of the patient other than the primary image, one box each,
 * with the registrations that place the image on the primary image under
 * it. A click on a registration displays the pair through it. A loaded
 * image on another frame of reference ends its list with New registration.
 * A loaded image on the primary image's frame says that it needs none and
 * comes last. A click on an image known only
 * from the session downloads it and selects it. With no other image, the
 * list offers to load one, which then gets a registration to the primary
 * image.
 */
/** The note a loaded image shows in place of New registration, by refusal. */
const FRAME_REFUSAL_NOTE = {
    sameFrame: 'workflow.imageRegistration.sameFOR',
    frameUnknown: 'registration.noFrameOfReference',
} as const;

export default function ImageList({
    primaryImage,
    availableImages,
    selectedImageUid,
    onSelectImage,
}: Props) {
    const intl = useIntl();
    const { processImportedFiles } = useProcessDicomFiles();
    const loadAvailableImage = useLoadAvailableImage();
    const activateRegistration = useActivateRegistration();
    const registrationState = useRegistrationState();
    const { showBusy, hideBusy } = useBusyIndicator();

    const handleDownloadImage = useCallback(
        async (image: AvailableImage) => {
            await showBusy(intl.formatMessage({ id: 'downloadingImage' }));
            try {
                const loadedImage = await loadAvailableImage(image);
                if (!loadedImage) {
                    reportImageDownloadFailure(intl);
                    return;
                }
                onSelectImage?.({
                    ...image,
                    isLoaded: true,
                    imageObject: loadedImage,
                });
            } catch (error) {
                reportImageDownloadFailure(intl, error);
            } finally {
                hideBusy();
            }
        },
        [intl, showBusy, hideBusy, loadAvailableImage, onSelectImage]
    );

    const sharesPrimaryFrame = useCallback(
        (image: AvailableImage) =>
            sharesFrameOfReference(
                image.imageObject?.frameOfReferenceUid,
                primaryImage.frameOfReferenceUid
            ),
        [primaryImage.frameOfReferenceUid]
    );

    const secondaryImages = useMemo(
        () =>
            availableImages
                .filter(
                    (img) => img.seriesInstanceUID !== primaryImage.seriesUid
                )
                .sort(
                    (a, b) =>
                        Number(sharesPrimaryFrame(a)) -
                        Number(sharesPrimaryFrame(b))
                ),
        [availableImages, primaryImage.seriesUid, sharesPrimaryFrame]
    );

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = event.target.files;
        if (!files?.length) {
            event.target.value = '';
            return;
        }

        const fileArray = Array.from(files);
        event.target.value = '';

        const result = await processImportedFiles(fileArray);
        const newImage = result?.images?.[0];

        if (!newImage || newImage.seriesUid === primaryImage.seriesUid) {
            return;
        }

        domainEventBus.publish(
            new RegistrationCreateRequestedEvent(
                primaryImage.seriesUid,
                newImage.seriesUid
            )
        );
    };

    const handleCreateRegistration = useCallback(
        (image: Image3D) => {
            domainEventBus.publish(
                new RegistrationCreateRequestedEvent(
                    primaryImage.seriesUid,
                    image.seriesUid
                )
            );
        },
        [primaryImage.seriesUid]
    );

    // The projection lists, per image, the registrations a drag on the
    // primary edits; nothing else can fuse the pair.
    const getRegistrationsForImage = (image: AvailableImage) =>
        registrationState.imageRegistrations.find(
            (r) => r.imageSeriesUid === image.seriesInstanceUID
        )?.registrations ?? [];

    return (
        <Box px={2} display="flex" flexDirection="column" gap={0.5}>
            <Box display="flex" alignItems="center" height={28}>
                <SectionTitle
                    id="workflow.imageRegistration.secondaryImages"
                    count={secondaryImages.length}
                />
            </Box>
            {secondaryImages.length === 0 ? (
                <Box display="flex" flexDirection="column" gap={1.5}>
                    <Typography fontSize={12} color="text.secondary">
                        <FormattedMessage id="workflow.imageRegistration.noSecondaryImagesRegistration" />
                    </Typography>
                    <Button
                        fullWidth
                        variant="outlined"
                        component="label"
                        startIcon={<FileUpload />}
                    >
                        <FormattedMessage id="loadImage" />
                        <input
                            type="file"
                            hidden
                            multiple
                            onChange={handleFileChange}
                        />
                    </Button>
                </Box>
            ) : (
                <Box display="flex" flexDirection="column" gap={1}>
                    {secondaryImages.map((image) => {
                        const loaded = image.imageObject;
                        const registrations = getRegistrationsForImage(image);
                        const sharesFOR = sharesPrimaryFrame(image);
                        // A registration maps one frame onto another: with
                        // either frame unknown, or both one frame, there is
                        // none to create.
                        const refusal =
                            loaded &&
                            frameRegistrationRefusal(
                                loaded.frameOfReferenceUid,
                                primaryImage.frameOfReferenceUid
                            );
                        const noteId = refusal
                            ? FRAME_REFUSAL_NOTE[refusal]
                            : undefined;

                        return (
                            <DatasetBox
                                key={image.seriesInstanceUID}
                                selected={
                                    selectedImageUid === image.seriesInstanceUID
                                }
                                data-testid="secondary-image-box"
                            >
                                <Box bgcolor="background.raised">
                                    <ImageSummaryRow
                                        image={image}
                                        hasSharedFrameOfReference={sharesFOR}
                                        onDownload={
                                            !loaded && !image.rawSeries
                                                ? () =>
                                                      void handleDownloadImage(
                                                          image
                                                      )
                                                : undefined
                                        }
                                    />
                                </Box>
                                {(loaded || registrations.length > 0) && (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        p={0.5}
                                        borderTop={1}
                                        borderColor="divider"
                                    >
                                        {registrations.map((registration) => (
                                            <RegistrationRow
                                                key={
                                                    registration.sopInstanceUid
                                                }
                                                label={
                                                    registration.contentLabel ||
                                                    registration.contentDescription ||
                                                    registration.sopInstanceUid
                                                }
                                                kind={
                                                    registration.registrationKind
                                                }
                                                approvalStatus={
                                                    registration.approvalStatus
                                                }
                                                selected={
                                                    registrationState.selectedSopInstanceUid ===
                                                        registration.sopInstanceUid &&
                                                    registrationState.selectedImageSeriesUid ===
                                                        image.seriesInstanceUID
                                                }
                                                onClick={() =>
                                                    activateRegistration(
                                                        registration.sopInstanceUid,
                                                        image.seriesInstanceUID
                                                    )
                                                }
                                            />
                                        ))}
                                        {loaded &&
                                            (noteId ? (
                                                <Typography
                                                    fontSize={12}
                                                    color="text.secondary"
                                                    pl={0.5}
                                                    pr={1}
                                                    py={0.5}
                                                    data-testid={
                                                        sharesFOR
                                                            ? 'same-frame-note'
                                                            : 'no-frame-note'
                                                    }
                                                >
                                                    <FormattedMessage
                                                        id={noteId}
                                                    />
                                                </Typography>
                                            ) : (
                                                <NewRegistrationRow
                                                    onClick={() =>
                                                        handleCreateRegistration(
                                                            loaded
                                                        )
                                                    }
                                                />
                                            ))}
                                    </Box>
                                )}
                            </DatasetBox>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
